import { format } from 'date-fns'
import type { ComponentType, SVGProps } from 'react'
import { UserX } from 'lucide-react'
import type {
  CleaningJob,
  PropertyCalendarBlock,
  PropertyCalendarDeposit,
  PropertyCalendarOrphanGap,
  PropertyCalendarReview,
  PropertyCalendarStay,
} from '@/api/client'
import { BLOCK_BAR_CLASSES, HOLD_BAR_CLASSES, blockLabel } from '@/components/calendar/block-bars'
import {
  CLEANING_STATE_META,
  NO_CREW_LABEL,
  cleaningState,
  isCleaningDrawn,
  isCrewMissing,
} from '@/components/calendar/cleaning-bars'
import { eventBlockMeta, type EventBlockInput } from '@/components/calendar/event-block-meta'
import { GAP_BAR_CLASSES } from '@/components/calendar/gap-bars'
import { packLanes } from '@/components/calendar/lanes'
import {
  CALENDAR_COLOR_CLASSES,
  getCalendarColorKey,
  parseStay,
} from '@/components/calendar/month-grid'
import { localDate } from '@/components/cleaning/presentation'
import { CALENDAR_LAYERS, type CalendarLayerId } from '@/components/property/calendar/layers'
import { buildDepositMarkers, type DepositMarker } from '@/lib/deposit-events'
import { formatCurrency, getChannelLabel } from '@/lib/format-utils'
import { nightsLabel } from '@/lib/gap-events'
import { isReservationCancelled } from '@/lib/reservation-utils'
import { buildReviewMarkers, reviewToday, type ReviewMarker } from '@/lib/review-events'
import { describeDelivery, type SupplyMarker } from '@/lib/supply-events'
import { parseCalendarDate } from '@/lib/calendar-date'
import type { DayAxis } from './day-axis'
import { dayNumber, dayOfDate } from './windows'
import type { PortfolioProperty } from './merge'

/**
 * The portfolio calendar's layout: for each property, what to draw and where,
 * in day units on the axis. Pure, and computed once per (loaded range, data):
 * scrolling and resizing only multiply these numbers by a day width, they
 * never recompute lanes (CAP-1416's "layout once, not per frame"). It takes
 * the properties merged across every window read (`merge.ts`), so an item that
 * spans windows is one bar.
 */

/**
 * The layers this grid draws: every one the property Calendar tab draws, in
 * its order (CAP-1417). Stays and blocks are the only bars; orphan gaps are a
 * hatched span of their nights; cleanings, deposits, supplies and reviews are
 * small markers in the day cell (the density rule of the approved mock).
 */
export const PORTFOLIO_LAYERS: readonly CalendarLayerId[] = CALENDAR_LAYERS.map(({ id }) => id)
export type PortfolioLayerId = CalendarLayerId

/** What the layout is asked to draw: which layers are on, and how many markers fit a day. */
export interface LayoutOptions {
  isVisible?: (id: CalendarLayerId) => boolean
  /** `GridDensity.markersPerDay`. */
  markersPerDay?: number
}

const everyLayer = () => true

/**
 * A stay or block holds nights: it runs from the middle of its first day to
 * the middle of its first free day (the check-out), so a same-day turnover
 * draws as two bars meeting mid-day, side by side in one lane.
 */
export const NIGHT_EDGE = 0.5

/**
 * What an item's tooltip says (CAP-1418): a heading, a few short lines, and a
 * warning in amber. Its accessible name (`title`) says the same in one line.
 */
export interface ItemTip {
  title: string
  lines: string[]
  warn?: string
}

/** "1 night", "5 nights". */
function nightsWords(nights: number): string {
  return `${nights} ${nights === 1 ? 'night' : 'nights'}`
}

interface PortfolioBarBase {
  key: string
  /** Day units on the axis, clipped to it: `0` is the left edge of `from`. */
  start: number
  end: number
  /** The whole days the bar touches, for culling to the visible days. */
  firstDay: number
  lastDay: number
  lane: number
  /** The bar's text where there is room for it... */
  label: string
  /** ...and on a phone, where a day is narrow. */
  shortLabel: string
  /** Everything about the bar in one line: its accessible name. */
  title: string
  tip: ItemTip
  className: string
  testId: string
  data: Record<string, string>
}

/**
 * One bar, carrying the item it was drawn from so a later ticket can open that
 * item's sheet from it (CAP-1418) without looking it up again.
 */
export type PortfolioBar =
  | (PortfolioBarBase & { kind: 'stay'; stay: PropertyCalendarStay })
  | (PortfolioBarBase & { kind: 'block'; block: PropertyCalendarBlock })
  | (PortfolioBarBase & { kind: 'gap'; gap: PropertyCalendarOrphanGap })

/**
 * How a marker looks and what it says: the property tab's own icon, colours
 * and words for the same thing (`CLEANING_STATE_META`, `eventBlockMeta`).
 */
type MarkerIcon = ComponentType<SVGProps<SVGSVGElement> & { strokeWidth?: number | string }>

interface MarkerLook {
  Icon: MarkerIcon
  /** Heavier for the two cleaning states that must read at a glance. */
  strokeWidth: number
  className: string
  /** Everything about it in one line: its accessible name. */
  title: string
  tip: ItemTip
  testId: string
  data: Record<string, string>
}

interface PortfolioMarkerBase extends MarkerLook {
  key: string
  /** The day it is on, as an axis index (for drawing; it moves as windows come and go)... */
  day: number
  /** ...and as a day number (`windows.ts`), which does not. */
  dayNumber: number
  /** Its place in the day cell, from the left: 0, 1, 2. */
  slot: number
}

/**
 * One marker, carrying the item it was drawn from, so CAP-1418 can open that
 * item's sheet from it (and its tooltip) without looking anything up again.
 */
export type PortfolioMarker =
  | (PortfolioMarkerBase & { kind: 'cleaning'; job: CleaningJob })
  | (PortfolioMarkerBase & {
      kind: 'deposit'
      deposit: PropertyCalendarDeposit
      event: DepositMarker
    })
  | (PortfolioMarkerBase & { kind: 'supply'; supply: SupplyMarker })
  | (PortfolioMarkerBase & { kind: 'review'; review: PropertyCalendarReview; event: ReviewMarker })

/**
 * A day with more markers than fit its cell: the last place says "+N". It
 * carries every marker of that day, the ones shown too, for the day's list
 * (CAP-1418).
 */
export interface PortfolioMoreMarker {
  kind: 'more'
  key: string
  day: number
  dayNumber: number
  slot: number
  /** How many are not shown. */
  count: number
  title: string
  tip: ItemTip
  markers: PortfolioMarker[]
}

export type PortfolioDayMarker = PortfolioMarker | PortfolioMoreMarker

export interface PortfolioLayerError {
  layer: PortfolioLayerId
  message: string
}

export interface PortfolioRow {
  propertyId: string
  name: string
  thumbnailUrl: string | null
  timeZone: string
  /** Lanes the bars (and gaps) use, from the whole loaded range, so a row never changes height as it scrolls. */
  laneCount: number
  bars: PortfolioBar[]
  /** Orphan gaps: under the bars, in the first lane, since nothing else holds those nights. */
  gaps: PortfolioBar[]
  /** The day markers along the row's foot, in day order. */
  markers: PortfolioDayMarker[]
  /** Layers this row could not read: said on the row, never drawn as "nothing booked". */
  errors: PortfolioLayerError[]
  /**
   * Layers this row read, but whose answer is not a clean one (a stale or
   * cut-short gap snapshot): said beside the errors, so a dated answer is
   * never taken for a current one.
   */
  caveats: PortfolioLayerError[]
}

type Span = { start: number; end: number; firstDay: number; lastDay: number }

/**
 * A run of nights from day number `startDay` to its first free day, as day
 * units on an axis starting on day number `axisStart`, or null when none of it
 * is on the axis.
 */
function nightSpan(
  axisStart: number,
  dayCount: number,
  startDay: number,
  freeDay: number
): Span | null {
  const first = startDay - axisStart
  const free = freeDay - axisStart
  if (free <= first) return null
  const start = Math.max(0, first + NIGHT_EDGE)
  const end = Math.min(dayCount, free + NIGHT_EDGE)
  if (end <= start) return null
  return { start, end, firstDay: Math.floor(start), lastDay: Math.ceil(end) - 1 }
}

const shortDate = (date: Date) => format(date, 'd MMM')

/**
 * What a bar says and how it looks, and the nights it holds as day numbers:
 * the same wherever the axis starts. Worked out once per item the API returned
 * (kept against the item itself), so a relayout when the grid gains a window
 * only places bars, it does not format every date and payout again.
 */
type BarContent = Omit<
  PortfolioBarBase,
  'key' | 'start' | 'end' | 'firstDay' | 'lastDay' | 'lane'
> & {
  startDay: number
  freeDay: number
}

const contents = new WeakMap<object, { key: string; content: unknown }>()

/**
 * What `build` makes of `item`, kept against the item itself. `key` is
 * whatever else the result depends on (a zone, the server's "today"): when it
 * changes, the content is made again.
 */
function contentOf<T extends object, C>(item: T, build: (item: T) => C, key = ''): C {
  const kept = contents.get(item)
  if (kept && kept.key === key) return kept.content as C
  const content = build(item)
  contents.set(item, { key, content })
  return content
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A day number as "28 Oct", the way the bars say dates, without a formatter per item. */
function shortDay(day: number): string {
  const date = new Date(day * 86_400_000)
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`
}

function stayContent(stay: PropertyCalendarStay): BarContent | null {
  // A cancelled stay holds no nights, exactly as on the property tab.
  if (isReservationCancelled(stay)) return null
  const parsed = parseStay(stay)
  if (!parsed) return null
  const guest = stay.guest_name || 'Guest'
  const payout = stay.total_payout === null ? null : formatCurrency(stay.total_payout)
  const colorKey = getCalendarColorKey(stay)
  const dates = `${shortDate(parsed.checkInDate)} to ${shortDate(parsed.checkOutDate)}`
  const startDay = dayOfDate(parsed.checkInDate)
  const freeDay = dayOfDate(parsed.checkOutDate)
  const channel = getChannelLabel(stay.channel)
  const nights = nightsWords(freeDay - startDay)
  // Only a known "yes": null is a thread that could not be read.
  const needsReply = stay.needs_reply === true
  return {
    startDay,
    freeDay,
    label: payout ? `${guest} · ${payout}` : guest,
    shortLabel: guest,
    title: [
      guest,
      channel,
      nights,
      dates,
      payout && `${payout} payout`,
      needsReply && 'needs reply',
    ]
      .filter(Boolean)
      .join(', '),
    tip: {
      title: guest,
      lines: [
        [channel, nights].filter(Boolean).join(' · '),
        dates,
        payout && `${payout} payout`,
      ].filter((line): line is string => !!line),
      ...(needsReply ? { warn: 'Waiting on a reply' } : {}),
    },
    className: CALENDAR_COLOR_CLASSES[colorKey],
    testId: 'portfolio-stay-bar',
    data: { 'data-reservation-id': stay.reservation_id, 'data-color-key': colorKey },
  }
}

function blockContent(block: PropertyCalendarBlock): BarContent | null {
  const start = parseCalendarDate(block.start)
  const freeDay = parseCalendarDate(block.end_exclusive)
  if (!start || !freeDay) return null
  const kindLabel = blockLabel(block.kind)
  const note = block.note?.trim() || null
  const startDay = dayOfDate(start)
  const free = dayOfDate(freeDay)
  const dates = `${shortDate(start)} to ${shortDate(freeDay)}`
  const nights = nightsWords(free - startDay)
  return {
    startDay,
    freeDay: free,
    label: note ?? kindLabel,
    shortLabel: note ?? kindLabel,
    title: [kindLabel, dates, nights, note].filter(Boolean).join(', '),
    tip: {
      title: kindLabel,
      lines: [note, `${dates} · ${nights}`].filter((line): line is string => !!line),
    },
    // As the property tab draws them: an owner block dashed grey, a hold made
    // in the Uplisting app striped.
    className: block.kind === 'uplisting' ? HOLD_BAR_CLASSES : BLOCK_BAR_CLASSES,
    testId: 'portfolio-block-bar',
    data: { 'data-block-kind': block.kind, 'data-block-id': block.id },
  }
}

function gapContent(gap: PropertyCalendarOrphanGap): BarContent | null {
  const startDay = dayNumber(gap.first_night)
  const lastNight = dayNumber(gap.last_night)
  if (startDay === null || lastNight === null) return null
  const span =
    startDay === lastNight
      ? `${nightsLabel(gap.nights)}, ${shortDay(startDay)}`
      : `${nightsLabel(gap.nights)}, ${shortDay(startDay)} to ${shortDay(lastNight)}`
  return {
    startDay,
    freeDay: lastNight + 1,
    // The property tab's words for a gap: its nights.
    label: nightsLabel(gap.nights),
    shortLabel: `${gap.nights}n`,
    title: `Orphan gap: ${span}${gap.actionable ? '. An offer is available' : ''}`,
    tip: {
      title: 'Orphan gap',
      lines: gap.actionable ? [span, 'An offer is available'] : [span],
    },
    className: GAP_BAR_CLASSES,
    testId: 'portfolio-gap-bar',
    data: { 'data-gap-first-night': gap.first_night },
  }
}

/** A marker before it is placed: the day it is on (a day number) and how it looks. */
interface MarkerContent extends MarkerLook {
  key: string
  dayNumber: number
}

/** Cleanings, reviews, deposits, supplies: the order a day cell lists them in (the mock's). */
const MARKER_ORDER = { cleaning: 0, review: 1, deposit: 2, supply: 3 } as const

type MarkerOf<K extends PortfolioMarker['kind']> = Extract<PortfolioMarker, { kind: K }>
type UnplacedMarker = {
  [K in PortfolioMarker['kind']]: Omit<MarkerOf<K>, 'day' | 'slot'>
}[PortfolioMarker['kind']]

/** A cleaning on its turnover day: the state's icon and colours, or "No crew" in amber. */
function cleaningMarker(job: CleaningJob, timeZone: string): UnplacedMarker | null {
  if (!isCleaningDrawn(job)) return null
  const day = dayNumber(job.local_date ?? localDate(job.scheduled_at, timeZone))
  if (day === null) return null
  const state = cleaningState(job)
  const meta = CLEANING_STATE_META[state]
  const noCrew = isCrewMissing(job)
  const words = noCrew ? `${meta.label} · ${NO_CREW_LABEL}` : meta.label
  return {
    kind: 'cleaning',
    job,
    key: `cleaning-${job.id}`,
    dayNumber: day,
    Icon: noCrew ? UserX : meta.Icon,
    strokeWidth: meta.strong ? 3 : 2.5,
    className: noCrew ? 'border-amber bg-transparent text-amber' : meta.classes,
    title: `Cleaning: ${words}, ${shortDay(day)}`,
    tip: { title: `Cleaning: ${words}`, lines: [shortDay(day)] },
    testId: 'portfolio-cleaning-marker',
    data: {
      'data-job-id': job.id,
      'data-cleaning-state': state,
      ...(noCrew ? { 'data-no-crew': 'true' } : {}),
    },
  }
}

/** An event in the property tab's words and colours ("Deposit hold expires"), on its day. */
function eventMarker(
  key: string,
  day: number,
  input: EventBlockInput,
  testId: string,
  data: Record<string, string>,
  detail: string | null = null
): MarkerContent {
  const meta = eventBlockMeta(input)
  return {
    key,
    dayNumber: day,
    Icon: meta.Icon,
    strokeWidth: meta.outlined ? 2.5 : 3,
    className: meta.classes,
    title: `${meta.words}, ${shortDay(day)}${detail ? ` (${detail})` : ''}`,
    tip: { title: meta.words, lines: detail ? [shortDay(day), detail] : [shortDay(day)] },
    testId,
    data: { ...data, 'data-tone': meta.tone, 'data-planned': meta.outlined ? 'true' : 'false' },
  }
}

function depositMarkers(deposit: PropertyCalendarDeposit, timeZone: string) {
  // The property tab's own choice of which events get a marker (one renewal of a run).
  return buildDepositMarkers([deposit], timeZone, () => true).flatMap(event => {
    const day = dayNumber(event.day)
    if (day === null) return []
    const content = eventMarker(
      `deposit-${event.key}`,
      day,
      { layer: 'deposit', type: event.type, planned: event.planned },
      'portfolio-deposit-marker',
      { 'data-event-type': event.type, 'data-reservation-id': event.reservationId }
    )
    return [{ ...content, kind: 'deposit' as const, deposit, event }]
  })
}

function supplyMarker(supply: SupplyMarker) {
  const day = dayNumber(supply.day)
  if (day === null) return null
  const detail = supply.kind === 'delivery' ? describeDelivery(supply.qty, supply.retailer) : null
  const content = eventMarker(
    `supply-${supply.key}`,
    day,
    { layer: 'supply', kind: supply.kind, category: supply.label, confidence: supply.confidence },
    'portfolio-supply-marker',
    { 'data-supply-kind': supply.kind, 'data-category': supply.category },
    detail || null
  )
  return { ...content, kind: 'supply' as const, supply }
}

function reviewMarkers(review: PropertyCalendarReview, today: string | null) {
  return buildReviewMarkers([review], { today, inDay: () => true }).flatMap(event => {
    const day = dayNumber(event.day)
    if (day === null) return []
    const content = eventMarker(
      `review-${event.key}`,
      day,
      { layer: 'review', kind: event.kind, planned: event.planned, owed: event.owed },
      'portfolio-review-marker',
      { 'data-review-kind': event.kind, 'data-reservation-id': event.reservationId }
    )
    return [{ ...content, kind: 'review' as const, review, event }]
  })
}

/**
 * Every marker a property's marker layers put on days. Each is made once and
 * kept against its item, so the layout and the chips' counts (once per day
 * scrolled) only walk them.
 */
function unplacedMarkers(
  property: PortfolioProperty,
  isVisible: (id: CalendarLayerId) => boolean
): UnplacedMarker[] {
  const { layers, time_zone: timeZone } = property
  const markers: UnplacedMarker[] = []
  if (isVisible('cleaning')) {
    for (const job of layers.cleaning.items) {
      const content = contentOf(job, item => cleaningMarker(item, timeZone), timeZone)
      if (content) markers.push(content)
    }
  }
  if (isVisible('reviews')) {
    const today = reviewToday(property.generated_at, timeZone)
    for (const review of layers.reviews.items) {
      markers.push(...contentOf(review, item => reviewMarkers(item, today), today ?? ''))
    }
  }
  if (isVisible('deposits')) {
    for (const deposit of layers.deposits.items) {
      markers.push(...contentOf(deposit, item => depositMarkers(item, timeZone), timeZone))
    }
  }
  if (isVisible('supplies')) {
    for (const supply of layers.supplies.items) {
      const content = contentOf(supply, supplyMarker)
      if (content) markers.push(content)
    }
  }
  return markers
}

/**
 * The markers on the axis, each day's in the mock's order, at most
 * `perDay` to a cell: a busier day shows one fewer and "+N" in the last place.
 */
function placeMarkers(
  unplaced: UnplacedMarker[],
  axisStart: number,
  dayCount: number,
  perDay: number
): PortfolioDayMarker[] {
  const byDay = new Map<number, UnplacedMarker[]>()
  for (const marker of unplaced) {
    const day = marker.dayNumber - axisStart
    if (day < 0 || day >= dayCount) continue
    const list = byDay.get(day)
    if (list) list.push(marker)
    else byDay.set(day, [marker])
  }
  const placed: PortfolioDayMarker[] = []
  for (const day of [...byDay.keys()].sort((a, b) => a - b)) {
    // Stable: within a layer, the order the item gave (a deposit's events in time).
    const list = byDay
      .get(day)!
      .sort((a, b) => MARKER_ORDER[a.kind] - MARKER_ORDER[b.kind])
      .map((marker, slot) => ({ ...marker, day, slot }) as PortfolioMarker)
    if (list.length <= perDay) {
      placed.push(...list)
      continue
    }
    const shown = list.slice(0, perDay - 1)
    const count = list.length - shown.length
    placed.push(...shown, {
      kind: 'more',
      key: `more-${day}`,
      day,
      dayNumber: axisStart + day,
      slot: shown.length,
      count,
      title: `${count} more on ${shortDay(axisStart + day)}: ${list
        .slice(shown.length)
        .map(marker => marker.title)
        .join('; ')}`,
      tip: {
        title: `${count} more on ${shortDay(axisStart + day)}`,
        lines: list.slice(shown.length).map(marker => marker.tip.title),
      },
      markers: list,
    })
  }
  return placed
}

function placed(content: BarContent | null, axisStart: number, dayCount: number) {
  if (!content) return null
  const span = nightSpan(axisStart, dayCount, content.startDay, content.freeDay)
  if (!span) return null
  const { label, shortLabel, title, tip, className, testId, data } = content
  return { label, shortLabel, title, tip, className, testId, data, ...span, lane: 0 }
}

export function buildPortfolioRow(
  property: PortfolioProperty,
  axis: DayAxis,
  { isVisible = everyLayer, markersPerDay = WIDE_DENSITY.markersPerDay }: LayoutOptions = {}
): PortfolioRow {
  const { layers } = property
  const errors: PortfolioLayerError[] = []
  // A layer switched off is not being relied on: its failure is not said on the row.
  for (const layer of PORTFOLIO_LAYERS) {
    const message = layers[layer].error
    if (message !== null && isVisible(layer)) errors.push({ layer, message })
  }
  const caveats: PortfolioLayerError[] = []
  for (const layer of PORTFOLIO_LAYERS) {
    if (!isVisible(layer)) continue
    for (const message of layers[layer].caveats) caveats.push({ layer, message })
  }

  // Stays first, then blocks: the order the property tab hands its grid, so
  // the same overlap stacks the same way on both screens. A layer one window
  // could not read still draws what the other windows read, beside its chip.
  const axisStart = dayOfDate(axis.start)
  const dayCount = axis.days.length
  const bars: PortfolioBar[] = []
  // Each bar's nights as day numbers, unclipped: lanes are packed on these, so
  // the stacking does not depend on where the axis starts. Packed on the
  // clipped ends instead, every bar running off the left edge would tie at 0,
  // and adding past days would make bars on screen swap lanes.
  const nights: { left: number; right: number }[] = []
  if (isVisible('stays')) {
    for (const stay of layers.stays.items) {
      const content = contentOf(stay, stayContent)
      const bar = placed(content, axisStart, dayCount)
      if (bar && content) {
        bars.push({ ...bar, kind: 'stay', stay, key: `stay-${stay.id}` })
        nights.push({ left: content.startDay, right: content.freeDay })
      }
    }
  }
  if (isVisible('blocks')) {
    for (const block of layers.blocks.items) {
      const content = contentOf(block, blockContent)
      const bar = placed(content, axisStart, dayCount)
      if (bar && content) {
        bars.push({ ...bar, kind: 'block', block, key: `block-${block.kind}-${block.id}` })
        nights.push({ left: content.startDay, right: content.freeDay })
      }
    }
  }

  const { lanes, laneCount } = packLanes(nights, span => span)
  bars.forEach((bar, index) => {
    bar.lane = lanes[index]
  })

  const gaps: PortfolioBar[] = []
  if (isVisible('orphan_gaps')) {
    for (const gap of layers.orphan_gaps.items) {
      const bar = placed(contentOf(gap, gapContent), axisStart, dayCount)
      if (bar) gaps.push({ ...bar, kind: 'gap', gap, key: `gap-${gap.first_night}` })
    }
  }

  return {
    propertyId: property.property_id,
    name: property.name,
    thumbnailUrl: property.thumbnail_url,
    timeZone: property.time_zone,
    // Gaps sit in the first lane: a row of gaps alone still has one, so its
    // error and caveat chips go below them, never over them.
    laneCount: Math.max(laneCount, gaps.length > 0 ? 1 : 0),
    bars,
    gaps,
    markers: placeMarkers(unplacedMarkers(property, isVisible), axisStart, dayCount, markersPerDay),
    errors,
    caveats,
  }
}

export function buildPortfolioRows(
  properties: readonly PortfolioProperty[],
  axis: DayAxis,
  options: LayoutOptions = {}
): PortfolioRow[] {
  return properties.map(property => buildPortfolioRow(property, axis, options))
}

/**
 * How many of each layer's items are on `firstDay`..`lastDay` (day numbers,
 * both included) across every property, whether the layer is on or off: what
 * each toggle chip says. A bar or gap counts when it holds a night there (the
 * API's own rule, `check_out > from`), a marker when its day is there. Null
 * for a layer some property could not read: a count missing that property's
 * items must not pass for the whole answer. Uses the same per-item content as
 * the layout, so it formats nothing: cheap enough to run once per day scrolled.
 */
export function countPortfolioLayers(
  properties: readonly PortfolioProperty[],
  firstDay: number,
  lastDay: number
): Record<CalendarLayerId, number | null> {
  const counts = Object.fromEntries(PORTFOLIO_LAYERS.map(id => [id, 0])) as Record<
    CalendarLayerId,
    number
  >
  const spans = (content: BarContent | null) =>
    content !== null && content.startDay <= lastDay && content.freeDay > firstDay ? 1 : 0
  const on = (marker: { dayNumber: number }) =>
    marker.dayNumber >= firstDay && marker.dayNumber <= lastDay
  for (const property of properties) {
    const { layers } = property
    for (const stay of layers.stays.items) counts.stays += spans(contentOf(stay, stayContent))
    for (const block of layers.blocks.items) counts.blocks += spans(contentOf(block, blockContent))
    for (const gap of layers.orphan_gaps.items) {
      counts.orphan_gaps += spans(contentOf(gap, gapContent))
    }
    for (const marker of unplacedMarkers(property, everyLayer)) {
      if (!on(marker)) continue
      if (marker.kind === 'cleaning') counts.cleaning++
      else if (marker.kind === 'deposit') counts.deposits++
      else if (marker.kind === 'supply') counts.supplies++
      else counts.reviews++
    }
  }
  const unread = new Set(
    PORTFOLIO_LAYERS.filter(id => properties.some(property => property.layers[id].error !== null))
  )
  return Object.fromEntries(
    PORTFOLIO_LAYERS.map(id => [id, unread.has(id) ? null : counts[id]])
  ) as Record<CalendarLayerId, number | null>
}

/**
 * How the grid is drawn at a width: a day's width, the property column's,
 * and a row's least height. Two densities, as in the approved mock.
 */
export interface GridDensity {
  narrow: boolean
  dayWidth: number
  asideWidth: number
  rowMinHeight: number
  /** Markers that fit one day cell (14px each, 15px apart, 3px in): "+N" takes the last place past it. */
  markersPerDay: number
}

export const WIDE_DENSITY: GridDensity = {
  narrow: false,
  dayWidth: 50,
  asideWidth: 212,
  rowMinHeight: 58,
  markersPerDay: 3,
}

/** Under 640px: the property column is the nickname alone. */
export const NARROW_DENSITY: GridDensity = {
  narrow: true,
  dayWidth: 40,
  asideWidth: 104,
  rowMinHeight: 52,
  markersPerDay: 2,
}

/** A bar is 22px tall, one lane every 25px, the first 6px below the row's top. */
export const BAR_HEIGHT = 22
export const LANE_PITCH = 25
export const LANE_TOP = 6
/**
 * Kept free under the bars for the day markers (cleanings, deposits, supplies,
 * reviews), so a row does not change height when they arrive or are switched
 * off.
 */
export const MARKER_STRIP = 22

/** A marker is a 14px square, its top 19px above the row's foot, one every 15px from 3px into its day. */
export const MARKER_SIZE = 14
export const MARKER_PITCH = 15
export const MARKER_INSET = 3
export const MARKER_BOTTOM = 19

/** Whether a row has anything to say about its reads: errors or caveats, in a lane of their own. */
export function hasNotes(row: PortfolioRow): boolean {
  return row.errors.length > 0 || row.caveats.length > 0
}

/** The lane a row's error chips sit in: below every bar, never over one. */
export function errorLane(row: PortfolioRow): number {
  return row.laneCount
}

export function rowHeight(row: PortfolioRow, density: GridDensity): number {
  const lanes = Math.max(1, row.laneCount + (hasNotes(row) ? 1 : 0))
  return Math.max(density.rowMinHeight, LANE_TOP + 2 + lanes * LANE_PITCH + MARKER_STRIP)
}
